import * as H from 'history'
import  React from 'react'
import { Alert } from 'react-bootstrap'
import { ClaimModel } from '../../../../models/ClaimModel'
import { Option } from '../../../../models/Option'
import { Representation } from '../../../../models/Representation'
import {
  AddressField, BooleanRadioButtonField, DateField, EmailField, MultiLineField, PhoneField, TextField, TextSelectField, validateFields, yesNoDataSource,
} from '../../../../shared/fields'
import { SalutationField } from '../../../../shared/fields/SalutationField'
import { ShowIf } from '../../../../shared/fields/ShowIf'
import { ISection, TableColumn, Table } from '../../../../shared/table'
import { formatNumber, getAddressString } from '../../../../shared/Utility'
import {
  calculateRepresentationTotal, sortRepresentationEntries, representationEntryColumns, RepresentationEntryDetails,
} from './RepresentationEntryDetails'
import { RepresentationEntry } from '../../../../models/RepresentationEntry'
import { Icon } from '../../../../shared/Icon'

export const representationColumns = (history: H.History, claimId: string, preview: boolean): TableColumn<Representation>[] => [
  { name: "Representative's name", content: (d) => `${d.firstName} ${d.lastName}` },
  { name: 'Company name', content: (d) => d.registeredName, responsive: 'md' },
  { name: 'Claimed amount', content: (d) => `$${formatNumber(calculateRepresentationTotal(d.entries))}` },
  { name: 'Claimed Before', content: (d) => (d.claimedBefore ? 'Yes' : 'No'), printOnly: true },
  { name: 'Address', content: (d) => getAddressString(d.physicalAddress), printOnly: true },
  { name: 'Mobile', content: (d) => d.mobile, printOnly: true },
  { name: 'Telephone', content: (d) => d.phone, printOnly: true },
  { name: 'Email ', content: (d) => d.email, printOnly: true },
  { name: 'Web site', content: (d) => d.website, printOnly: true },
  { name: 'Relationship', content: (d) => d.relationship, printOnly: true },
  { name: 'Description', content: (d) => d.description, printOnly: true },
  {
    name: '', content: (d) => (preview ? '' : <a href="#" onClick={(e) => { e.preventDefault(); history.push(`/claim/${claimId}/prepare/representation/${d.id}`) }}>Edit</a>),
  },
]

export const calculateAllRepresentativesTotal = (reps: Representation[]): number => {
  let total = 0
  for (const rep of reps) {
    total += calculateRepresentationTotal(rep.entries)
  }
  return total
}

export interface RepresentationDetailsProps {
    id: string
    model: Representation
    onChange: { (v: Partial<Representation>): any }
    preview?: boolean
    claim: ClaimModel
    addressCountries: Option<string>[]
    markets: Option<string>[]
}

export interface RepresentationDetailsState {
}

export class RepresentationDetails extends React.Component<RepresentationDetailsProps, RepresentationDetailsState> implements ISection {
    title: SalutationField
    firstName: TextField
    lastName: TextField
    claimedBefore: BooleanRadioButtonField
    registeredName: TextField
    tradingName: TextField
    physicalAddress: AddressField
    postalAddressDifferent: BooleanRadioButtonField
    postalAddress: AddressField
    phone: PhoneField
    mobile: PhoneField
    email: EmailField
    website: TextField
    relationship: TextSelectField
    otherRelationship: TextField
    representedFrom: DateField
    representedTo: DateField
    description: MultiLineField
    table: Table<RepresentationEntry>

    constructor(props: RepresentationDetailsProps) {
      super(props)

      this.state = {
      }
    }

    validate = (callback: { (res: boolean): any }) => {
      validateFields((res) => callback(res),
        this.title,
        this.firstName,
        this.lastName,
        this.claimedBefore,
        this.registeredName,
        this.tradingName,
        this.physicalAddress,
        this.postalAddressDifferent,
        this.postalAddress,
        this.phone,
        this.mobile,
        this.email,
        this.website,
        this.relationship,
        this.otherRelationship,
        this.representedFrom,
        this.representedTo,
        this.description)
    }

    saveAndClose = (callback?: { (res: boolean): any }) => {
      callback && callback(true)
    }

    isDuplicate() {
      const key = (`${this.props.model.firstName}|${this.props.model.lastName}|${this.props.model.registeredName}`).toLowerCase()

      return this.props.claim.representations.filter((r) => (`${r.firstName}|${r.lastName}|${r.registeredName}`).toLowerCase() == key && r.id != this.props.model.id).length > 0
            || this.props.claim.marketingConsultants.filter((r) => (`${r.firstName}|${r.lastName}|${r.tradingName}`).toLowerCase() == key).length > 0
    }

    render() {
      const relationshipDataSource = [
        { value: 'Agent', label: 'Agent' },
        { value: 'Branch office', label: 'Branch office' },
        { value: 'Director / Shareholder', label: 'Director / Shareholder' },
        { value: 'Distributor', label: 'Distributor' },
        { value: 'Related / Associated company', label: 'Related / Associated company' },
        { value: 'Relative', label: 'Relative' },
        { value: 'Other', label: 'Other' },
      ]

      let { relationship } = this.props.model
      if (relationship && relationshipDataSource.filter((r) => r.value == relationship).length == 0) {
        relationship = 'Other'
      }

      const entryColumns = [...representationEntryColumns(this.props.markets)]

      const invalidRows = this.props.model.entries.filter((r) => r.invalid).length > 0
      if (invalidRows) {
        entryColumns.splice(0, 0, { name: '', content: (d) => (d.invalid ? <Icon type="warning" /> : <span />) })
      }

      return (
        <>
          <SalutationField
            ref={(r) => this.title = r}
            id={`${this.props.id}-title`}
            label="Title"
            value={this.props.model.title}
            onChange={(title) => this.props.onChange({ title })}
            required
            preview={this.props.preview}
          />
          <div className="clearfix">
            <TextField
              ref={(r) => this.firstName = r}
              id={`${this.props.id}-firstName`}
              label="First name"
              value={this.props.model.firstName}
              onChange={(firstName) => this.props.onChange({ firstName })}
              required
              maxLength={50}
              preview={this.props.preview}
              className="float-left mr-3"
            />
            <TextField
              ref={(r) => this.lastName = r}
              id={`${this.props.id}-lastName`}
              label="Last (Family) name"
              value={this.props.model.lastName}
              onChange={(lastName) => this.props.onChange({ lastName })}
              required
              maxLength={50}
              preview={this.props.preview}
              className="float-left"
            />
          </div>
          <BooleanRadioButtonField
            ref={(r) => this.claimedBefore = r}
            id={`${this.props.id}-claimedBefore`}
            label="Have you claimed this representative before?"
            value={this.props.model.claimedBefore}
            onChange={(claimedBefore) => this.props.onChange({ claimedBefore })}
            dataSource={yesNoDataSource}
            required
            preview={this.props.preview}
          />
          <TextField
            ref={(r) => this.registeredName = r}
            id={`${this.props.id}-registeredName`}
            label="Company name"
            value={this.props.model.registeredName}
            onChange={(registeredName) => this.props.onChange({ registeredName })}
            required
            maxLength={80}
            preview={this.props.preview}
            errorMessage={this.isDuplicate() ? 'This contact name and company name are identical to another Overseas Representative, or a Marketing Consultant, for the applicant. Please change either the contact name or company name to distinguish between the two entities' : ''}
          />
          <TextField
            ref={(r) => this.tradingName = r}
            id={`${this.props.id}-tradingName`}
            label="Business or trading name (if different to the Registered business name)"
            value={this.props.model.tradingName}
            maxLength={80}
            onChange={(tradingName) => this.props.onChange({ tradingName })}
            preview={this.props.preview}
          />
          <AddressField
            ref={(r) => this.physicalAddress = r}
            id={`${this.props.id}-physicalAddress`}
            label="Business address (physical address)"
            value={this.props.model.physicalAddress}
            onChange={(physicalAddress) => this.props.onChange({ physicalAddress })}
            required
            preview={this.props.preview}
            countries={this.props.addressCountries}
          />
          <BooleanRadioButtonField
            ref={(r) => this.postalAddressDifferent = r}
            id={`${this.props.id}-postalAddressDifferent`}
            label="Is your postal address different to the address above?"
            value={this.props.model.postalAddressDifferent}
            onChange={(postalAddressDifferent) => this.props.onChange({ postalAddressDifferent })}
            dataSource={yesNoDataSource}
            required
            preview={this.props.preview}
          />
          <AddressField
            ref={(r) => this.postalAddress = r}
            id={`${this.props.id}-postalAddress`}
            label="Postal address"
            value={this.props.model.postalAddress}
            onChange={(postalAddress) => this.props.onChange({ postalAddress })}
            required={this.props.model.postalAddressDifferent}
            visible={this.props.model.postalAddressDifferent || false}
            preview={this.props.preview}
            countries={this.props.addressCountries}
          />
          <PhoneField
            ref={(r) => this.phone = r}
            id={`${this.props.id}-phone`}
            label="Telephone number (e.g. +61 2 9999 9999)"
            value={this.props.model.phone}
            onChange={(phone) => this.props.onChange({ phone })}
            required
            block={false}
            preview={this.props.preview}
          />
          <PhoneField
            ref={(r) => this.mobile = r}
            id={`${this.props.id}-mobile`}
            label="Mobile phone number (e.g. +61 499 999 999)"
            value={this.props.model.mobile}
            onChange={(mobile) => this.props.onChange({ mobile })}
            block={false}
            preview={this.props.preview}
          />
          <EmailField
            ref={(r) => this.email = r}
            id={`${this.props.id}-email`}
            label="Email"
            value={this.props.model.email}
            onChange={(email) => this.props.onChange({ email })}
            required
            maxLength={80}
            preview={this.props.preview}
          />
          <TextField
            ref={(r) => this.website = r}
            id={`${this.props.id}-website`}
            label="Business website address"
            value={this.props.model.website}
            onChange={(website) => this.props.onChange({ website })}
            required={false}
            maxLength={80}
            preview={this.props.preview}
          />
          <ShowIf condition={!this.props.preview}>
            <TextSelectField
              ref={(r) => this.relationship = r}
              id={`${this.props.id}-relationship`}
              label="What is the relationship of the representative to you?"
              value={relationship}
              onChange={(relationship) => this.props.onChange({ relationship })}
              dataSource={relationshipDataSource}
              required
              preview={this.props.preview}
            />
            <TextField
              ref={(r) => this.otherRelationship = r}
              id={`${this.props.id}-otherRelationship`}
              label="Other relationship"
              value={this.props.model.relationship == 'Other' ? '' : this.props.model.relationship}
              onChange={(relationship) => this.props.onChange({ relationship })}
              visible={relationship == 'Other'}
              required={relationship == 'Other'}
              maxLength={40}
              preview={this.props.preview}
            />
          </ShowIf>
          <div className="clearfix">
            <DateField
              ref={(r) => this.representedFrom = r}
              id={`${this.props.id}-representedFrom`}
              label="Representation from"
              value={this.props.model.representedFrom}
              onChange={(representedFrom) => this.props.onChange({ representedFrom })}
              required
              preview={this.props.preview}
              className="float-left mr-3"
            />
            <DateField
              ref={(r) => this.representedTo = r}
              id={`${this.props.id}-representedTo`}
              label="to"
              value={this.props.model.representedTo}
              onChange={(representedTo) => this.props.onChange({ representedTo })}
              required
              preview={this.props.preview}
              className="float-left"
            />
          </div>
          <MultiLineField
            ref={(r) => this.description = r}
            id={`${this.props.id}-description`}
            label="Describe all of your representative's activities"
            value={this.props.model.description}
            onChange={(description) => this.props.onChange({ description })}
            required
            maxLength={255}
            preview={this.props.preview}
          />

          <h3>Overseas representative expenses</h3>
          <div style={{ display: this.props.preview ? 'none' : '' }}>
            <p className="mt-3">The basis for any apportionment of expenses should be entered in the Notes field.</p>
            <div className="mt-3 mb-3">
              <Alert className="emdg-page-alerts">
                <p>This category is capped at $200,000 but you should claim all eligible expenses.</p>
              </Alert>
            </div>
          </div>

          <Table<RepresentationEntry>
            id="table"
            label="Schedule 1A: Overseas representation"
            ref={(r) => this.table = r}
            value={this.props.model.entries.sort((a, b) => sortRepresentationEntries(a, b))}
            onChange={(entries) => this.props.onChange({ entries })}
            columns={entryColumns}
            renderSection={(p, i) => <RepresentationEntryDetails {...p} markets={this.props.markets} claim={this.props.claim} preview={this.props.preview} />}
            importUrl="/api/representation/import"
            exportUrl="/api/representation/export"
            match={(a, b) => a.id == b.id}
            total={calculateRepresentationTotal}
            allowEdit
            allowImport
            invalidRows={invalidRows}
          />
        </>
      )
    }
}
